import { useWorkspace } from '@/features/workspaces/hooks';
import { DATA_SOURCES_DATA, DataSourceIDEnum, DataSourceType, EDataSourceCategory } from '@/models/dataSource';
import { CustomConnectorDto } from '@/models/connectors';
import { useCompanyValues } from '@/providers';
import { useGetDataSourceTypesQuery } from '@/services/dataSources';
import { useGetWorkspaceConnectorsQuery } from '@/services/endpoints/workspace-connectors';
import { useMemo } from 'react';
import { PLACEHOLDER_SOURCE_CONNECTORS } from './placeholders';
import { resolveDataSourceTypeForConnector } from './sourceConnectorMatching';

export type SourceConnectorCatalogItem = {
  dataSourceType: DataSourceType;
  connector: CustomConnectorDto;
  isAuthenticated: boolean;
  isPlaceholder: boolean;
};

const SYNTHETIC_SOURCE_CONNECTOR_TYPES: DataSourceType[] = [
  {
    id: DataSourceIDEnum.D365BusinessCentral,
    name: 'Microsoft Dynamics 365 Business Central',
    subtitle: '',
    logoUrl: '',
    category: EDataSourceCategory.SOURCE_CONNECTOR,
    configuration: {},
  },
];

export const useSourceConnectorCatalogItems = () => {
  const { id: workspaceId } = useWorkspace();
  const { currentLanguage: lang } = useCompanyValues();

  const { data: dataSourceTypes = [], isFetching: isFetchingTypes } = useGetDataSourceTypesQuery();
  const {
    data: liveConnectors = [],
    isFetching: isFetchingConnectors,
    isError: isConnectorsError,
  } = useGetWorkspaceConnectorsQuery({
    workspaceId,
    lang,
    queryParams: { direction: 'INPUT' },
  });

  return useMemo(() => {
    const isPlaceholder = isConnectorsError;
    const connectors = isPlaceholder ? PLACEHOLDER_SOURCE_CONNECTORS : liveConnectors;

    const sourceConnectorTypesFromApi = dataSourceTypes.filter((type) => type.category === EDataSourceCategory.SOURCE_CONNECTOR);

    const sourceConnectorTypes = sourceConnectorTypesFromApi.length > 0 ? sourceConnectorTypesFromApi : SYNTHETIC_SOURCE_CONNECTOR_TYPES;

    const standardTypes = dataSourceTypes.filter((type) => type.category !== EDataSourceCategory.SOURCE_CONNECTOR);

    const sourceConnectorItems: SourceConnectorCatalogItem[] = connectors
      .map((connector) => {
        const dataSourceType = resolveDataSourceTypeForConnector(connector, sourceConnectorTypes);
        if (!dataSourceType) return null;

        return {
          dataSourceType,
          connector,
          isAuthenticated: connector.isAuthenticated,
          isPlaceholder,
        };
      })
      .filter((item): item is SourceConnectorCatalogItem => item !== null);

    return {
      standardTypes,
      sourceConnectorItems,
      isLoading: isFetchingTypes || isFetchingConnectors,
      isPlaceholder,
    };
  }, [dataSourceTypes, isConnectorsError, isFetchingConnectors, isFetchingTypes, liveConnectors]);
};

export const isSourceConnectorType = (type: DataSourceType) =>
  type.category === EDataSourceCategory.SOURCE_CONNECTOR || DATA_SOURCES_DATA[type.id as DataSourceIDEnum]?.category === EDataSourceCategory.SOURCE_CONNECTOR;
